import { Avatar, Button, Loader, Select, Stack, TextInput, tokens } from '@hotjar/hotsauce';
import { useTranslation } from '@hotjar/translations';
import styled from '@emotion/styled';
import { useEffect, useRef } from 'react';
import { browserHistory } from '@hotjar/webapp/utils/routing/browser-history';
import { AvatarSection, DimensionAlert, SectionHeader, StyledField, Wrapper } from '../styles';
import { roles } from './roles';
import { SuccessMessageWithIcon } from '../../../components';
import { useAccountDetailForm } from './useAccountDetailForm';

const MessageWrapper = styled.div`
  margin-top: ${tokens.spacer.x1};
  margin-bottom: ${tokens.spacer.x1pt5};
`;

export const AccountDetailForm = (): JSX.Element => {
  const { t } = useTranslation('profile');
  const fileRef = useRef<HTMLInputElement>(null);
  const {
    user,
    register,
    handleSubmit,
    errors,
    isDirty,
    watch,
    onSave,
    onChangePicture,
    isLoading,
    isCurrentUserLoading,
  } = useAccountDetailForm();

  useEffect(() => {
    register('picture');
  }, [register]);

  const name = user?.name ?? '';
  const avatar = watch('picture');

  if (isCurrentUserLoading) {
    return (
      <Wrapper data-testid="account-detail-form-loader">
        <Loader isLoading size="xl" />
      </Wrapper>
    );
  }

  return (
    <Wrapper data-testid="account-detail-form">
      <SectionHeader textType="header5" as="h1">
        {t('account-detail.title')}
      </SectionHeader>
      <form onSubmit={handleSubmit(onSave)}>
        <Stack preset="form">
          <StyledField
            labelText={t('account-detail.avatar')}
            inputId="avatar"
            invalidText={errors.picture?.message}
            isInvalid={!!errors.picture}
          >
            <AvatarSection>
              <div className="avatar-content">
                <Avatar userName={name} size="xl" src={avatar} />
              </div>
              <div className="upload-button-content">
                <Button kind="secondary" size="s" onClick={() => fileRef?.current?.click()}>
                  {t('account-detail.choose')}
                </Button>
                <input
                  id="avatar"
                  name="avatar"
                  type="file"
                  className="hidden"
                  ref={fileRef}
                  onChange={onChangePicture}
                />
                <DimensionAlert>{t('account-detail.ideal-dimension')}</DimensionAlert>
              </div>
            </AvatarSection>
          </StyledField>

          <StyledField
            inputId="name"
            labelText={t('account-detail.name')}
            invalidText={errors?.name?.message}
            isInvalid={!!errors.name}
            isRequired
          >
            <TextInput
              id="name"
              name="name"
              ref={register({ required: t<string>('account-detail.name-error') })}
            />
          </StyledField>

          <StyledField
            inputId="role"
            labelText={t('account-detail.role')}
            helperText={t('account-detail.role-help')}
          >
            <Select
              id="role"
              name="role"
              ref={register({ required: t('account-detail.role') as string })}
            >
              <option value="">{t('account-detail.select')}</option>
              {roles.map((role) => (
                <option key={`Role_${role.id}`} value={role.id}>
                  {role.id === 'other' ? t('account-detail.other') : role.value}
                </option>
              ))}
            </Select>
          </StyledField>
          <StyledField inputId="email" labelText={t('account-detail.email')}>
            {user?.isEmailVerified === true && (
              <MessageWrapper>
                <SuccessMessageWithIcon message="Verified" />
              </MessageWrapper>
            )}
            <TextInput id="email" name="email" ref={register({})} isReadonly />
          </StyledField>
          <Stack>
            <Button isDisabled={!isDirty} isLoading={isLoading} type="submit" role="button">
              {t('account-detail.save')}
            </Button>
            <Button kind="secondary" onClick={() => browserHistory.push('/profile/email')}>
              {t('account-detail.change-email')}
            </Button>
          </Stack>
        </Stack>
      </form>
    </Wrapper>
  );
};
